import { memo, useCallback, useContext, useEffect } from 'react';
import { ARTIFACT_TYPES, CopilotContext } from '@Sage/ssm-web5';
import { downloadCopilotArtifact } from '@Sage/ssm-web5/lib/services/copilotAgents';
import { useDispatch } from 'react-redux';

import { EntityModalActions } from 'actions';
import { COPILOT_DOMAINS, getCopilotActions, getCopilotFunctions } from 'utils/copilotRegistry';
import { COPILOT_TRANSFORMERS } from 'utils/copilotRegistry/transformers';
import { getActiveCrud } from 'utils/crud';
import { translateObjectB4ToB2 } from 'utils/fm/fields';
import { getEntityFromString } from 'utils/getEntityFromString';
import { getLiteral } from 'utils/getLiteral';
import { hashHistory } from 'utils/routes';

const CRUD_MODAL_TITLE_LITERAL_BY_ENTITY = {
    accounts: 'page_title_accounts_create',
    activities: 'title_add_activity',
    calendar: 'title_create_event',
    contacts: 'page_title_contacts_create',
    opportunities: 'page_title_opportunities_create',
    salesorders: 'title_add_salesorder',
    task: 'action_create_task',
};

const CRUD_MODAL_MIDDLE_BUTTON_LITERAL_BY_ENTITY = {
    calendar: 'action_save_and_list',
    task: 'action_save_and_list',
};

/**
 * Headless component that registers web4-side Copilot integrations
 * (feature methods, AI-callable functions, artifact handlers) with the
 * shared `CopilotContext`.
 *
 * Mounted unconditionally inside `Web5ProviderContext` so registrations
 * are wired up regardless of which Copilot UI feature flags
 * (`COPILOT_BAR`, `COPILOT_SIDEBAR`, ...) are enabled for the user.
 */
const CopilotRegistrations = () => {
    const dispatch = useDispatch();
    const { getMethodsForCopilot } = useContext(CopilotContext);

    const handleDashboardArtifact = useCallback((artifact) => {
        const dashboardId = artifact?.props?.id;
        if (dashboardId) {
            hashHistory.push(`/metrics/dashboards/view/${String(dashboardId)}`);
        }
    }, []);

    const handleFormEntityArtifact = useCallback(
        (artifact) => {
            // Open entity modal with form data from Copilot artifact
            if (artifact?.download) {
                downloadCopilotArtifact(artifact.download)
                    .then((artifactData) => {
                        const entity = getEntityFromString(artifactData.entity);
                        const rawFormData = artifactData.params;
                        const { configMap, mappedSchema } = getActiveCrud();

                        if (!mappedSchema) {
                            console.warn('form_entity artifact missing active CRUD schema context');
                            return;
                        }

                        // Apply same transformation as CRUD scenario:
                        // 1. Translate B4 (API) format to B2 (internal) format
                        // 2. Apply entity-specific transformations
                        const translatedData = translateObjectB4ToB2(entity, rawFormData, {
                            configMap,
                            mappedSchema,
                        });
                        const transformer = COPILOT_TRANSFORMERS[entity.trueName];
                        const transformedData = transformer
                            ? transformer(translatedData)
                            : translatedData;

                        // Dispatch modal open with transformed form data
                        dispatch(
                            EntityModalActions.init({
                                entity,
                                data: transformedData,
                                labels: {
                                    title: getLiteral(
                                        CRUD_MODAL_TITLE_LITERAL_BY_ENTITY[entity.trueName],
                                    ),
                                    success: getLiteral('succes_entitycreatedsuccessfully'),
                                    error: getLiteral('label_failed_create'),
                                    middleButtonText: getLiteral(
                                        CRUD_MODAL_MIDDLE_BUTTON_LITERAL_BY_ENTITY[
                                            entity.trueName
                                        ] || 'action_save_and_detail',
                                    ),
                                },
                            }),
                        );
                    })
                    .catch((error) => {
                        console.error('Failed to download form_entity artifact:', error);
                    });
            } else {
                console.warn('form_entity artifact missing download descriptor', artifact);
            }
        },
        [dispatch],
    );

    useEffect(() => {
        if (!getMethodsForCopilot) return;
        getMethodsForCopilot.registerFeatureMethods(
            COPILOT_DOMAINS.SSM,
            getCopilotActions(COPILOT_DOMAINS.SSM),
        );
        getMethodsForCopilot.registerFunctions(getCopilotFunctions());
        getMethodsForCopilot.registerArtifactHandlers({
            [ARTIFACT_TYPES.DASHBOARD]: handleDashboardArtifact,
            [ARTIFACT_TYPES.FORM_ENTITY]: handleFormEntityArtifact,
        });
    }, [getMethodsForCopilot, handleDashboardArtifact, handleFormEntityArtifact]);

    return null;
};

export default memo(CopilotRegistrations);
